/*
 * Campaign detail — shows what the campaign was created with, in the same
 * order as the create wizard (Basics → Contacts → Script → Settings (incl. retry mode) →
 * Schedule (incl. pause timings) → SMS & Webhooks), plus live progress. Data comes from
 * GET /v2/voice-campaigns/:id (see voice-campaign-repository findOne);
 * contacts and disposition submissions are paged from their own endpoints.
 * strict_timings is intentionally never shown.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Copy, BarChart3, Pencil, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DataTable } from '@/components/data-table/DataTable';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import {
  API_VOICE_CAMPAIGNS_GET_ONE,
  API_VOICE_CAMPAIGNS_CONTACTS,
  API_VOICE_CAMPAIGNS_CONTACT_GROUPS,
  API_VOICE_CAMPAIGNS_CALL_GROUP_AGENTS,
  API_VOICE_CAMPAIGNS_AGENTS,
  API_VOICE_CAMPAIGNS_DISPOSITION_SUBMISSIONS,
  API_PROMPT_ACTIVE_LIST,
  API_CAMPAIGN_REPORT,
} from '@/service/url';
import { CAMPAIGN_TYPE_MAP, TEMPLATE_TYPE_MAP, ROUTES, MODULE_ACL } from '@/utilities/constant';
import { useModuleAcl } from '@/hooks/useModuleAcl';
import { canCreateCampaign } from '@/utilities/promptUploadAccess';
import { stripHtmlTags } from '@/utilities/helpers';
import { formatDispositionCallDirectionLabel } from '@/utilities/dispositionCallDirection';
import CampaignBasesSection from './CampaignBasesSection';
import { canEditCampaignNow } from './editLock';
import { pauseLabel, type PauseSlot } from './PauseSlotsModal';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { AdditionalDetailsCell, detailsFromText } from '@/features/contact-group/custom-fields/AdditionalDetailsCell';
import { useCustomFieldsVisible } from '@/features/contact-group/custom-fields/useCustomFieldsVisible';

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = any;

const STATUS: Record<number, { label: string; tone: string }> = {
  0: { label: 'Ready', tone: 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300' },
  1: { label: 'Running', tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300' },
  2: { label: 'Completed', tone: 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300' },
  3: { label: 'Low Balance', tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300' },
  4: { label: 'Error', tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300' },
  5: { label: 'Error', tone: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300' },
  6: { label: 'Cancelling', tone: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300' },
  7: { label: 'Cancelled', tone: 'bg-pink-100 text-pink-700 ring-pink-200 dark:bg-pink-950/40 dark:text-pink-300' },
  8: { label: 'Pausing', tone: 'bg-cyan-100 text-cyan-700 ring-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300' },
  9: { label: 'Paused', tone: 'bg-slate-100 text-slate-700 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300' },
  10: { label: 'Resuming', tone: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300' },
  21: { label: 'DND Processing', tone: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-950/40 dark:text-orange-300' },
  22: { label: 'Waiting for Channel', tone: 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300' },
};

const PROMPT_LABELS: Record<string, string> = {
  welcome: 'Welcome',
  menu: 'Menu',
  noinput: 'No Input',
  wronginput: 'Wrong Input',
  thanks: 'Thanks',
  noagent: 'No Agent Available',
};

const WEBHOOK_EVENT_LABELS: Record<string, string> = {
  CALL_CONNECTED: 'SMS when call connects',
  CALL_FAILED: 'SMS when call fails',
  VALID_DTMF: 'SMS on valid DTMF press',
  PATCH_CONNECTED: 'SMS when patch connects',
  PATCH_FAILED: 'SMS when patch fails',
  HANGUP: 'Webhook on hangup',
  DTMF: 'Webhook on DTMF',
  CONNECTED_CALLS: 'Webhook on connected calls',
};

const DAY_ORDER = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const num = (v: unknown) => Number(v) || 0;
const pct = (part: number, whole: number) => (whole > 0 ? `${Math.round((part / whole) * 1000) / 10}%` : '—');

function fmtDate(value: unknown, withTime = false) {
  if (!value) return '—';
  const d = new Date(String(value));
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

const fmtTime = (value: unknown) => (value ? String(value).slice(0, 5) : '—');

function fmtDuration(totalSec: number) {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

function parseList(value: unknown): Row[] {
  if (Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

const JsonBox = ({ data }: { data: unknown }) => {
  if (data === null || data === undefined || data === '') return <>—</>;
  let parsed = data;
  try {
    parsed = typeof data === 'string' ? JSON.parse(data) : data;
  } catch {
    /* keep raw string */
  }
  return (
    <pre className="m-0 max-h-32 max-w-[280px] overflow-auto rounded-md border border-border/60 p-2 text-xs">
      {typeof parsed === 'object' ? JSON.stringify(parsed, null, 2) : String(parsed)}
    </pre>
  );
};

/* ── Layout bits ───────────────────────────────────────────────── */

const Section = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <section className="rounded-lg border border-border/60 bg-card p-4">
    <div className="mb-3">
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
    </div>
    {children}
  </section>
);

const Fields = ({ items }: { items: Array<[string, ReactNode] | false | null | undefined> }) => (
  <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
    {items.filter(Boolean).map((item) => {
      const [label, value] = item as [string, ReactNode];
      return (
        <div key={label} className="min-w-0">
          <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</dt>
          <dd className="mt-0.5 break-words text-sm text-foreground">
            {value === null || value === undefined || value === '' ? '—' : value}
          </dd>
        </div>
      );
    })}
  </dl>
);

const Chips = ({ values, empty = '—' }: { values: string[]; empty?: string }) =>
  values.length ? (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v) => (
        <span key={v} className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-foreground">
          {v}
        </span>
      ))}
    </div>
  ) : (
    <>{empty}</>
  );

// Daily pause slots (campaign_schedule.pause_slots), styled like the create wizard's chips.
const PauseChips = ({ slots }: { slots: PauseSlot[] }) =>
  slots.length ? (
    <div className="flex flex-wrap gap-1.5">
      {slots.map((s, i) => (
        <span
          key={`${s.start_time}-${i}`}
          className="inline-flex items-center rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-medium tabular-nums text-amber-700 dark:text-amber-300"
        >
          <span className="mr-1 font-semibold">{pauseLabel(s, i)}</span>
          {fmtTime(s.start_time)}–{fmtTime(s.end_time)}
        </span>
      ))}
    </div>
  ) : (
    <>None</>
  );

const Stat = ({ label, value, hint }: { label: string; value: ReactNode; hint?: string }) => (
  <div className="rounded-lg border border-border/60 bg-card px-4 py-3">
    <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="mt-1 text-xl font-semibold tabular-nums text-foreground">{value}</div>
    {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
  </div>
);

const yesNo = (v: unknown) => (v ? 'Yes' : 'No');

/*
 * One server page at a time. `url` answers { rows, total, total_capped? } for
 * ?page&limit[&search&after&count]. A known next page goes by keyset (`after` =
 * last id) and the total is fetched once per search; a page jump falls back to
 * offset.
 */
const PagedTable = ({
  tableId,
  url,
  columns,
  searchable = false,
  refreshable = false,
  rowKey = 'id',
  searchPlaceholder = 'Search name or number...',
  emptyTitle,
}: {
  tableId: string;
  url: string;
  columns: ColumnDef<Row>[];
  searchable?: boolean;
  refreshable?: boolean;
  rowKey?: string;
  searchPlaceholder?: string;
  emptyTitle: string;
}) => {
  const [reloadTick, setReloadTick] = useState(0);
  // url carries the campaign id, so the saved search/page are per campaign and per table.
  const [search, setSearch] = usePersistedState(`campaigns/CampaignDetail/${url}:search`, '');
  const [query, setQuery] = usePersistedState(`campaigns/CampaignDetail/${url}:query`, '');
  const [paging, setPaging] = usePersistedState(`campaigns/CampaignDetail/${url}:paging`, { pageIndex: 0, pageSize: 25 });
  const [rows, setRows] = useState<Row[]>([]);
  const [total, setTotal] = useState(0);
  const [totalCapped, setTotalCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqSeq = useRef(0);
  const cursors = useRef({ key: '', map: new Map<number, number>(), hasTotal: false });
  // Last applied search: the restored search must not reset the restored page on mount.
  const appliedQuery = useRef(query);

  useEffect(() => {
    const t = setTimeout(() => {
      const next = search.trim();
      if (next === appliedQuery.current) return;
      appliedQuery.current = next;
      setQuery(next);
      setPaging((p) => (p.pageIndex ? { ...p, pageIndex: 0 } : p));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    // Refresh re-counts too (new tick -> new key -> fresh cursors + total).
    const key = `${url}|${query}|${paging.pageSize}|${reloadTick}`;
    if (cursors.current.key !== key) cursors.current = { key, map: new Map(), hasTotal: false };
    const cur = cursors.current;
    const params = new URLSearchParams({ page: String(paging.pageIndex + 1), limit: String(paging.pageSize) });
    if (query) params.set('search', query);
    const after = cur.map.get(paging.pageIndex);
    if (after) params.set('after', String(after));
    if (cur.hasTotal) params.set('count', '0');
    getApiCall(`${url}?${params}`, {})
      .then((res: Row) => {
        if (seq !== reqSeq.current) return;
        const list: Row[] = res?.rows || [];
        setRows(list);
        if (list.length) cur.map.set(paging.pageIndex + 1, list[list.length - 1].id);
        if (res?.total != null) {
          setTotal(num(res.total));
          setTotalCapped(Boolean(res.total_capped));
          cur.hasTotal = true;
        }
      })
      .catch(() => {
        if (seq === reqSeq.current) setRows([]);
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [url, query, paging, reloadTick]);

  return (
    <div className="space-y-2">
      <DataTable
        tableId={tableId}
        columns={columns}
        data={rows}
        isLoading={loading}
        pageSize={25}
        serverPagination={{ ...paging, rowCount: total, onChange: setPaging }}
        getRowId={(r: Row) => String(r[rowKey])}
        leftContent={
          searchable || refreshable ? (
            <div className="flex items-center gap-2">
              {searchable && (
                <Input
                  className="h-8 w-64"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              )}
              {refreshable && (
                <Button variant="outline" size="sm" disabled={loading} onClick={() => setReloadTick((t) => t + 1)} title="Refresh">
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              )}
            </div>
          ) : undefined
        }
        disableSearch
        disableExport
        emptyTitle={query ? 'Nothing matches your search' : emptyTitle}
      />
      {totalCapped && (
        <p className="text-xs text-muted-foreground">
          More than {total.toLocaleString()} match. Search by name or number to find the rest.
        </p>
      )}
    </div>
  );
};

/* ── Page ──────────────────────────────────────────────────────── */

type TabKey = 'overview' | 'agents' | 'contacts' | 'webhooks' | 'dispositions';

const AGENTS_PER_PAGE = 10; // must match getCampaignCallGroupAgents limit

const Pill = ({ on, label }: { on: boolean; label: string }) => (
  <span
    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${
      on
        ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
        : 'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800/60 dark:text-slate-300'
    }`}
  >
    {label}
  </span>
);

const isDashboardIn = (a: Row) => Boolean(a.login_at) && !a.logout_at;
const ProfileLoginPill = ({ a }: { a: Row }) => {
  const on = String(a.login_status) === '1';
  return <Pill on={on} label={on ? `Logged in${a.active_profile ? ` · ${a.active_profile}` : ''}` : 'Logged out'} />;
};
const DashboardLoginPill = ({ a }: { a: Row }) => (
  <Pill on={isDashboardIn(a)} label={isDashboardIn(a) ? 'Logged in' : 'Logged out'} />
);
const accessLabel = (v: unknown) => (v === 'team_lead' ? 'Team Lead' : v === 'regular' ? 'Agent' : '—');

const CallGroupDetails = ({ campaignId, cg }: { campaignId: string; cg: Row }) => {
  const [live, setLive] = useState<{ group: Row; agents: Row[]; total: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const seq = useRef(0);
  const groupId = cg.call_group_id;

  const load = (p = page) => {
    if (!campaignId || !groupId) return;
    const mine = ++seq.current;
    setLoading(true);
    getApiCall(
      API_VOICE_CAMPAIGNS_CALL_GROUP_AGENTS.replace(':id', campaignId).replace(':groupId', String(groupId)),
      { page: p },
    )
      .then((resp: Row) => {
        if (mine !== seq.current) return;
        const rows = Array.isArray(resp?.agents) ? resp.agents : [];
        // Agents removed since last load left this page empty -> back to page 1.
        if (!rows.length && p > 1) {
          setPage(1);
          return;
        }
        setLive({ group: resp?.group || null, agents: rows, total: num(resp?.total) });
      })
      .catch(() => {})
      .finally(() => {
        if (mine === seq.current) setLoading(false);
      });
  };

  useEffect(() => load(page), [campaignId, groupId, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const g: Row = live?.group || cg.agent_group_campaign_details || {};
  const waits = String(g.should_wait_or_hangup ?? '1') !== '0';
  const secs = (v: unknown) => (v === null || v === undefined || v === '' ? null : `${v}s`);
  const snapshot: Row[] = live
    ? []
    : (cg.agent_group_campaign_details?.agent_schedule_mappings || []).map((m: Row) => m.agent_vc).filter(Boolean);
  const agents: Row[] = live ? live.agents : snapshot.slice((page - 1) * AGENTS_PER_PAGE, page * AGENTS_PER_PAGE);
  const total = live ? live.total : snapshot.length;
  const pages = Math.max(1, Math.ceil(total / AGENTS_PER_PAGE));

  return (
    <div className="space-y-3 py-2">
      <div className="flex items-start justify-between gap-3">
        <Fields
          items={[
            ['Ring Strategy', g.strategy],
            ['Agent Ringing Timeout', secs(g.all_agent_ringing_timeout)],
            ['If No Agent Free', waits ? 'Wait in queue' : 'Hang up'],
            waits && ['Max Wait Time', secs(g.max_wait_time)],
            ['Sticky (Multi-IVR)', yesNo(g.sticky)],
            ['Sticky Agent', g.sticky_member === 'hard_sticky' ? 'Hard' : g.sticky_member === 'soft_sticky' ? 'Soft' : 'Off'],
            // ['Voicemail', yesNo(g.is_voicemail_enabled)],
          ]}
        />
        <Button variant="outline" size="sm" onClick={() => load()} disabled={loading} title="Refresh">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>
      {agents.length ? (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="py-1.5 pr-4 font-medium">Agent</th>
              <th className="py-1.5 pr-4 font-medium">Agent Number</th>
              <th className="py-1.5 pr-4 font-medium">Access</th>
              <th className="py-1.5 pr-4 font-medium">Profile Login</th>
              <th className="py-1.5 font-medium">Dashboard Login</th>
            </tr>
          </thead>
          <tbody>
            {agents.map((a: Row) => (
              <tr key={a.id} className="border-t border-border/60">
                <td className="py-1.5 pr-4">{a.agent_name || '—'}</td>
                <td className="py-1.5 pr-4">{a.agent_number || '—'}</td>
                <td className="py-1.5 pr-4">{accessLabel(a.access)}</td>
                <td className="py-1.5 pr-4">
                  <ProfileLoginPill a={a} />
                </td>
                <td className="py-1.5">{live ? <DashboardLoginPill a={a} /> : '…'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p className="text-sm text-muted-foreground">{loading ? 'Loading agents…' : 'No agents in this group.'}</p>
      )}
      {total > AGENTS_PER_PAGE && (
        <div className="flex items-center justify-end gap-2 text-sm text-muted-foreground">
          <span>
            {(page - 1) * AGENTS_PER_PAGE + 1}–{Math.min(page * AGENTS_PER_PAGE, total)} of {total.toLocaleString()}
          </span>
          <Button variant="outline" size="sm" disabled={loading || page <= 1} onClick={() => setPage(page - 1)}>
            Prev
          </Button>
          <span>
            Page {page} / {pages}
          </span>
          <Button variant="outline" size="sm" disabled={loading || page >= pages} onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default function CampaignDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const showCustomFields = useCustomFieldsVisible();
  const [tab, setTab] = usePersistedState<TabKey>(`campaigns/CampaignDetail/${id}:tab`, 'overview');
  const [legacyBases, setLegacyBases] = useState(false);
  const [campaign, setCampaign] = useState<Row | null>(null);
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState<Row[]>([]);
  const [totalTalkSec, setTotalTalkSec] = useState(0);
  const { canAdd, canEdit } = useModuleAcl(MODULE_ACL.CAMPAIGNS);
  const canDuplicate = canAdd && canCreateCampaign();
  const canEditCampaign = canEdit && canCreateCampaign(); // backend also allows Ready / Paused only

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getApiCall(API_VOICE_CAMPAIGNS_GET_ONE.replace(':id', id), {})
      .then((resp: Row) => setCampaign(resp || null))
      .catch(() => setCampaign(null))
      .finally(() => setLoading(false));
    // Talk time = sum of patch_duration across legs, from the report summary.
    getApiCall(`${API_CAMPAIGN_REPORT.replace(':id', id)}?page=1&limit=1&include_summary=1`, {})
      .then((resp: Row) => setTotalTalkSec(num(resp?.summary?.total_talk_duration)))
      .catch(() => {});
    getApiCall(`${API_PROMPT_ACTIVE_LIST}/all`, {})
      .then((resp: Row) => setPrompts(Array.isArray(resp) ? resp : []))
      .catch(() => {});
  }, [id]);


  const c = campaign || {};
  const type = String(c.campaign_type ?? '');
  const template = String(c.template_id ?? '');
  const isClick2Call = type === '1';
  const isAutoDialer = type === '2';
  const isPowerDialer = type === '4';
  const hasBases = ['2', '3', '4'].includes(type); // dialers that read voice_base
  const isAgentFirst = isAutoDialer && String(c.first_call ?? '').toLowerCase() === 'agent';
  const showsTemplate = !!type && type !== '1' && type !== '3';
  const showsMenuSettings = template === '1' || (template === '2' && num(c.enable_call_patch_additional_settings) === 1);

  /* Derived lists */
  const dids = useMemo(
    () =>
      (c.campaign_numbers || [])
        .map((n: Row) => {
          const d = n.number_details_data;
          const label = d?.actual_number || n.cli;
          return label ? `${label}${d?.number_type ? ` (${d.number_type})` : ''}` : null;
        })
        .filter(Boolean) as string[],
    [c.campaign_numbers],
  );

  const contactGroups = useMemo(
    () =>
      (c.campaign_contact_group || [])
        .map((g: Row) => g.voice_campaign_contact_group_details?.group_name)
        .filter(Boolean) as string[],
    [c.campaign_contact_group],
  );

  const callGroups: Row[] = c.voice_campaigns_call_group_details || [];
  const dtmfMappings: string[] = callGroups
    .filter((cg: Row) => cg.dtmf != null && String(cg.dtmf) !== '')
    .map((cg: Row) => `${cg.dtmf} → ${cg.agent_group_campaign_details?.group_name || `#${cg.call_group_id}`}`);

  const hasAgents = callGroups.some((cg: Row) =>
    (cg.agent_group_campaign_details?.agent_schedule_mappings || []).some((m: Row) => m.agent_vc),
  );

  const schedule: Row = (c.voice_campaigns_schedule || [])[0] || null;
  const weekDays: string[] = [...(schedule?.week_days || [])].sort(
    (a: string, b: string) => DAY_ORDER.indexOf(a) - DAY_ORDER.indexOf(b),
  );

  const pauseSlots: PauseSlot[] = Array.isArray(schedule?.pause_slots)
    ? schedule.pause_slots.filter((s: Row) => s?.start_time && s?.end_time)
    : [];

  const promptById = useMemo(() => new Map(prompts.map((p: Row) => [String(p.id), p])), [prompts]);
  const promptRows: Row[] = (c.campaigns || []).map((cfg: Row) => {
    const p = promptById.get(String(cfg.value));
    const url = Array.isArray(p?.prompt_url) ? p.prompt_url[0]?.url : p?.prompt_url;
    return {
      key: `${cfg.name}-${cfg.value}`,
      role: template === '0' && cfg.name === 'welcome' ? 'Voice File' : PROMPT_LABELS[cfg.name] || cfg.name,
      name: p?.prompt_name || `Prompt #${cfg.value}`,
      language: cfg.language || p?.language,
      url,
    };
  });

  const webhookRows: Row[] = c.voice_campaign_webhook_details || [];
  const dispositionForm: Row = c.dispositionForm || null;
  const dispositionFields = parseList(dispositionForm?.fields);
  const script: Row = c.voice_campaigns_scripts_details || null;

  /* Progress — same definitions as before */
  const total = num(c.total_nos);
  const dnd = num(c.dnd_count);
  const processed = num(c.nos_processed);
  const dialed = processed + num(c.auto_retry_count);
  const connected = num(c.connected_calls);
  const failed = Math.max(0, dialed - connected);
  const pending = Math.max(0, total - dnd - processed);
  const progress = total - dnd > 0 ? Math.min(100, Math.round((processed / (total - dnd)) * 100)) : 0;

  if (loading) return <Loader />;

  if (!campaign) {
    return (
      <ScrollPage>
        <PageHeader
          title="Campaign Detail"
          actions={
            <Button variant="outline" onClick={() => navigate(`/${ROUTES.CAMPAIGNS.LIST}`)}>
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          }
        />
        <p className="text-sm text-muted-foreground">Campaign not found or you do not have access to it.</p>
      </ScrollPage>
    );
  }

  const status = STATUS[num(c.campaign_status)];

  /* Columns */
  const callGroupColumns: ColumnDef<Row>[] = [
    { id: 'name', header: 'Call Group', cell: ({ row }) => row.original.agent_group_campaign_details?.group_name || `#${row.original.call_group_id}` },
    { id: 'strategy', header: 'Ring Strategy', cell: ({ row }) => row.original.agent_group_campaign_details?.strategy || '—' },
    ...(template === '1' || callGroups.some((cg: Row) => cg.dtmf != null && String(cg.dtmf) !== '')
      ? [{ id: 'dtmf', header: 'DTMF Key', cell: ({ row }) => (row.original.dtmf != null && String(row.original.dtmf) !== '' ? row.original.dtmf : '—') } as ColumnDef<Row>]
      : []),
    {
      id: 'agents',
      header: 'Agents',
      cell: ({ row }) => (row.original.agent_group_campaign_details?.agent_schedule_mappings || []).length,
    },
  ];

  const renderCallGroupDetails = (cg: Row) => <CallGroupDetails campaignId={id || ''} cg={cg} />;

  const contactGroupColumns: ColumnDef<Row>[] = [
    {
      id: 'name',
      header: 'Contact Group',
      cell: ({ row }) => `${row.original.group_name || `#${row.original.id}`}${row.original.is_deleted ? ' (deleted)' : ''}`,
    },
    {
      id: 'contacts',
      header: 'Contacts',
      cell: ({ row }) => `${num(row.original.contact_count).toLocaleString()}${row.original.count_capped ? '+' : ''}`,
    },
    { id: 'created', header: 'Created', cell: ({ row }) => fmtDate(row.original.created_at) },
  ];

  const agentColumns: ColumnDef<Row>[] = [
    { id: 'agent', header: 'Agent', cell: ({ row }) => row.original.agent_name || '—' },
    { id: 'number', header: 'Agent Number', cell: ({ row }) => row.original.agent_number || '—' },
    { id: 'email', header: 'Email', cell: ({ row }) => row.original.email_id || '—' },
    { id: 'group', header: 'Call Group', cell: ({ row }) => row.original.group_name || `#${row.original.agent_group_id}` },
    { id: 'access', header: 'Access', cell: ({ row }) => accessLabel(row.original.access) },
    { id: 'profile', header: 'Profile Login', cell: ({ row }) => <ProfileLoginPill a={row.original} /> },
    { id: 'dashboard', header: 'Dashboard Login', cell: ({ row }) => <DashboardLoginPill a={row.original} /> },
  ];

  const contactColumns: ColumnDef<Row>[] = [
    { id: 'name', header: 'Name', cell: ({ row }) => [row.original.first_name, row.original.last_name].filter(Boolean).join(' ') || '—' },
    { accessorKey: 'primary_number', header: 'Primary Number' },
    { accessorKey: 'alt1_number', header: 'Alt 1 Number' },
    { accessorKey: 'alt2_number', header: 'Alt 2 Number' },
    { accessorKey: 'company', header: 'Company' },
    { accessorKey: 'city', header: 'City' },
    ...(showCustomFields
      ? [
          {
            id: 'customer_additional_details',
            header: 'Customer Additional Details',
            cell: ({ row }) => (
              <AdditionalDetailsCell
                details={detailsFromText(row.original.customer_additional_details)}
                contactName={[row.original.first_name, row.original.last_name].filter(Boolean).join(' ') || undefined}
              />
            ),
          } as ColumnDef<Row>,
        ]
      : []),
  ];

  const webhookColumns: ColumnDef<Row>[] = [
    { id: 'event', header: 'When', cell: ({ row }) => WEBHOOK_EVENT_LABELS[row.original.event] || row.original.event || '—' },
    { id: 'name', header: 'Name', cell: ({ row }) => row.original.sms_webhook?.webhook_name || row.original.webhook?.webhook_name || '—' },
    { id: 'url', header: 'URL', cell: ({ row }) => row.original.sms_webhook?.url || row.original.webhook?.url || row.original.url || '—' },
    { id: 'method', header: 'Method', cell: ({ row }) => row.original.sms_webhook?.request_type || row.original.req_type || '—' },
    { id: 'payload', header: 'Payload', cell: ({ row }) => <JsonBox data={row.original.sms_webhook?.payload ?? row.original.webhook?.body_json ?? row.original.payload} /> },
    { id: 'headers', header: 'Headers', cell: ({ row }) => <JsonBox data={row.original.sms_webhook?.header ?? row.original.webhook?.header_json} /> },
  ];

  const dispositionFieldColumns: ColumnDef<Row>[] = [
    { accessorKey: 'field_name', header: 'Field' },
    { accessorKey: 'field_type', header: 'Type' },
    { id: 'required', header: 'Required', cell: ({ row }) => yesNo(row.original.is_required) },
    { id: 'cdr', header: 'Attach to CDR', cell: ({ row }) => yesNo(row.original.attach_to_cdr) },
  ];

  const dispositionDataColumns: ColumnDef<Row>[] = [
    { id: 'date', header: 'Date', cell: ({ row }) => fmtDate(row.original.created_at, true) },
    { id: 'agent', header: 'Agent', cell: ({ row }) => row.original.agent?.agent_name || row.original.agent_id || '—' },
    { id: 'agent_number', header: 'Agent Number', cell: ({ row }) => row.original.agent?.agent_number || '—' },
    { id: 'form', header: 'Form', cell: ({ row }) => row.original.disposition_form?.name || '—' },
    { id: 'data', header: 'Data', cell: ({ row }) => <JsonBox data={row.original.data} /> },
  ];

  /* Tabs */
  const tabs: Array<{ key: TabKey; label: string; count?: number }> = [
    { key: 'overview', label: 'Overview' },
    ...(hasAgents ? [{ key: 'agents' as TabKey, label: 'Agents' }] : []),
    ...(contactGroups.length && (!hasBases || legacyBases) ? [{ key: 'contacts' as TabKey, label: 'Contacts' }] : []),
    ...(webhookRows.length ? [{ key: 'webhooks' as TabKey, label: 'SMS & Webhooks', count: webhookRows.length }] : []),
    { key: 'dispositions', label: 'Disposition Submissions' },
  ];

  const overview = (
    <div className="space-y-4">
      {(num(c.campaign_status) === 4 || num(c.campaign_status) === 5) && c.error_desc ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
          <strong>Error:</strong> {c.error_desc}
        </div>
      ) : null}

      {/* Progress */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-8">
        <Stat label="Total Contacts" value={total} />
        <Stat label="Calls Dialed" value={dialed} hint={num(c.auto_retry_count) ? `incl. ${num(c.auto_retry_count)} retries` : undefined} />
        <Stat label="Answered" value={connected} hint={pct(connected, dialed) + ' of dialed'} />
        <Stat label="Failed" value={failed} />
        <Stat label="Pending" value={pending} />
        <Stat label="DND Skipped" value={dnd} />
        <Stat label="Total Talk Time" value={fmtDuration(totalTalkSec)} />
        <Stat label="Avg Talk / Answered" value={connected ? fmtDuration(totalTalkSec / connected) : '—'} />
      </div>
      <div>
        <div className="mb-1 flex justify-between text-xs text-muted-foreground">
          <span>Progress</span>
          <span>
            {processed} / {Math.max(0, total - dnd)} processed ({progress}%)
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <Section title="Basics" description="Dialer, template and ownership chosen when the campaign was created.">
        <Fields
          items={[
            ['Campaign Name', c.campaign_name],
            ['Campaign ID', c.campaign_id],
            ['Dialer Type', (CAMPAIGN_TYPE_MAP as Record<string, string>)[type] || type],
            isAutoDialer && ['First Call Connects', isAgentFirst ? 'Agent First' : 'Customer First'],
            showsTemplate && ['Template', (TEMPLATE_TYPE_MAP as Record<string, string>)[template] || template],
            isPowerDialer && ['Pacing Ratio (agent : customer)', c.pacing_ratio],
            template === '3' && ['IVR Flow', c.context || (c.ivr ? `#${c.ivr}` : null)],
            template === '2' && !isAgentFirst && ['Additional Settings', num(c.enable_call_patch_additional_settings) ? 'On' : 'Off'],
            ['Company', c.campaignCompany?.name],
            ['Call Centre', c.campaignCallCenter?.name],
            ['Created On', fmtDate(c.created_at, true)],
            ['Last Updated', fmtDate(c.updated_at, true)],
            num(c.updated_count) > 0 && ['Times Edited', num(c.updated_count)],
          ]}
        />
      </Section>

      {(showsMenuSettings || promptRows.length > 0) && (
        <Section title="Voice & IVR" description="Prompts played on the call and how key presses are handled.">
          {showsMenuSettings && (
            <div className="mb-4">
              <Fields
                items={[
                  template === '1' && ['Valid DTMF Keys', <Chips values={String(c.dtmf || '').split(',').map((s) => s.trim()).filter(Boolean)} />],
                  dtmfMappings.length > 0 && ['DTMF → Call Group', <Chips values={dtmfMappings} />],
                  ['Menu Wait Time', c.menu_wait_time != null ? `${c.menu_wait_time}s` : null],
                  ['Re-Prompt Count', c.re_prompt],
                ]}
              />
            </div>
          )}
          {promptRows.length > 0 ? (
            <div className="divide-y divide-border/60 rounded-md border border-border/60">
              {promptRows.map((p) => (
                <div key={p.key} className="flex flex-wrap items-center gap-3 px-3 py-2">
                  <span className="w-40 shrink-0 text-xs font-medium uppercase tracking-wide text-muted-foreground">{p.role}</span>
                  <span className="min-w-0 flex-1 text-sm">
                    {p.name}
                    {p.language ? <span className="ml-2 text-xs text-muted-foreground">({p.language})</span> : null}
                  </span>
                  {p.url ? <audio controls preload="none" src={p.url} className="h-8 max-w-[260px]" /> : null}
                </div>
              ))}
            </div>
          ) : null}
        </Section>
      )}

      {callGroups.length > 0 && (
        <Section
          title={isClick2Call ? 'Queues' : 'Call Groups'}
          description="Agent groups that receive calls from this campaign."
        >
          <DataTable
            tableId="campaign-detail.call-groups"
            columns={callGroupColumns}
            data={callGroups}
            pageSize={10}
            disableSearch
            disableExport
            emptyTitle="No call groups"
            renderSubRow={renderCallGroupDetails}
          />
        </Section>
      )}

      {hasBases ? (
        <Section title="Uploaded Bases" description="Number files this campaign dials.">
          <CampaignBasesSection
            campaignId={c.campaign_id}
            campaignName={c.campaign_name}
            allowStatusChange={canEdit}
            onLoaded={(res) => setLegacyBases(!!res?.legacy)}
          />
        </Section>
      ) : (
        <Section title="Contacts" description="Who this campaign dials.">
          <Fields
            items={[
              ['Source', contactGroups.length ? 'Contact group' : '—'],
              ['Total Numbers', total],
            ]}
          />
          {contactGroups.length > 0 && (
            <div className="mt-3">
              <PagedTable
                tableId="campaign-detail.contact-groups"
                url={API_VOICE_CAMPAIGNS_CONTACT_GROUPS.replace(':id', String(c.campaign_id))}
                columns={contactGroupColumns}
                searchable
                refreshable
                searchPlaceholder="Search group name..."
                emptyTitle="No contact groups"
              />
            </div>
          )}
        </Section>
      )}

      <Section title="Script" description="Shown to agents during the call.">
        {script ? (
          <Fields
            items={[
              ['Title', script.title],
              ['Short Description', stripHtmlTags(script.short_description)],
              ['Description', stripHtmlTags(script.description)],
            ]}
          />
        ) : (
          <p className="text-sm text-muted-foreground">No script attached.</p>
        )}
      </Section>

      <Section title="Settings" description="Caller ID, pacing between calls and retries.">
        <Fields
          items={[
            ['Caller ID (DID)', <Chips values={dids} />],
            ['Time Between Calls', `${num(c.time_between_call)}s`],
            ['Retry', num(c.is_retry) ? 'Enabled' : 'Disabled'],
            num(c.is_retry) > 0 && [
              'Retry Mode',
              c.retry_mode === 'after_completion' ? 'Retry after all numbers are called' : 'Retry on failure',
            ],
            num(c.is_retry) > 0 && ['Max Retries', num(c.max_retries)],
            num(c.is_retry) > 0 && ['Retry Interval', `${num(c.retry_interval)} min`],
          ]}
        />
      </Section>

      <Section title="Schedule" description="When the campaign is allowed to dial.">
        <Fields
          items={[
            ['Start Date', fmtDate(c.start_date)],
            ['End Date', fmtDate(c.end_date)],
            ['Calling Hours', `${fmtTime(schedule?.start_time || c.start_hours)} – ${fmtTime(schedule?.end_time || c.end_hours)}`],
            ['Week Days', <Chips values={weekDays} />],
            ['Pause Timings', <PauseChips slots={pauseSlots} />],
          ]}
        />
      </Section>

      <Section title="Disposition Form" description="Form agents fill after each call.">
        {dispositionForm ? (
          <>
            <Fields
              items={[
                ['Name', dispositionForm.name],
                ['Call Direction', formatDispositionCallDirectionLabel(dispositionForm.call_direction)],
                ['Fields', dispositionFields.length],
              ]}
            />
            {dispositionFields.length > 0 && (
              <div className="mt-3">
                <DataTable
                  tableId="campaign-detail.disposition-fields"
                  columns={dispositionFieldColumns}
                  data={dispositionFields}
                  pageSize={10}
                  disableSearch
                  disableExport
                  emptyTitle="No fields"
                />
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            No disposition form selected. Agents see only the basic fields (name, number, remarks).
          </p>
        )}
      </Section>

      <Section title="SMS & Webhooks" description="Notifications fired on call events.">
        {webhookRows.length ? (
          <Fields
            items={webhookRows.map((w: Row) => [
              WEBHOOK_EVENT_LABELS[w.event] || w.event || 'Webhook',
              w.sms_webhook?.webhook_name || w.webhook?.webhook_name || w.url || '—',
            ])}
          />
        ) : (
          <p className="text-sm text-muted-foreground">No SMS or webhooks configured.</p>
        )}
      </Section>
    </div>
  );

  const body = {
    overview,
    agents: (
      <PagedTable
        tableId="campaign-detail.agents"
        url={API_VOICE_CAMPAIGNS_AGENTS.replace(':id', String(c.campaign_id))}
        columns={agentColumns}
        searchable
        refreshable
        rowKey="row_key"
        searchPlaceholder="Search name, number or email..."
        emptyTitle="No agents"
      />
    ),
    contacts: (
      <PagedTable
        tableId="campaign-detail.contacts"
        url={API_VOICE_CAMPAIGNS_CONTACTS.replace(':id', String(c.campaign_id))}
        columns={contactColumns}
        searchable
        emptyTitle="No contacts in the linked groups"
      />
    ),
    webhooks: (
      <DataTable tableId="campaign-detail.webhooks" columns={webhookColumns} data={webhookRows} pageSize={15} disableExport emptyTitle="No webhooks" />
    ),
    dispositions: (
      <PagedTable
        tableId="campaign-detail.disposition-data"
        url={API_VOICE_CAMPAIGNS_DISPOSITION_SUBMISSIONS.replace(':id', String(c.campaign_id))}
        columns={dispositionDataColumns}
        emptyTitle="No disposition submissions yet"
      />
    ),
  }[tab];

  return (
    <ScrollPage>
      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span>{c.campaign_name || 'Campaign Detail'}</span>
            {status ? (
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ${status.tone}`}>
                {status.label}
              </span>
            ) : null}
          </span>
        }
        description={[
          (CAMPAIGN_TYPE_MAP as Record<string, string>)[type],
          showsTemplate ? (TEMPLATE_TYPE_MAP as Record<string, string>)[template] : null,
          `ID ${c.campaign_id}`,
        ]
          .filter(Boolean)
          .join(' · ')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate(`/${ROUTES.CAMPAIGNS.REPORT.replace(':id', String(c.campaign_id))}`)}>
              <BarChart3 className="h-4 w-4" /> Report
            </Button>
            {canEditCampaign && canEditCampaignNow(c) && (
              <Button variant="outline" onClick={() => navigate(`/${ROUTES.CAMPAIGNS.EDIT.replace(':edit_camp_id', String(c.campaign_id))}`)}>
                <Pencil className="h-4 w-4" /> Edit
              </Button>
            )}
            {canDuplicate && (
              <Button variant="outline" onClick={() => navigate(`/${ROUTES.CAMPAIGNS.DUPLICATE.replace(':duplicate_camp_id', String(c.campaign_id))}`)}>
                <Copy className="h-4 w-4" /> Duplicate
              </Button>
            )}
            <Button variant="outline" onClick={() => navigate(`/${ROUTES.CAMPAIGNS.LIST}`)}>
              <ArrowLeft className="h-4 w-4" /> Back
            </Button>
          </div>
        }
      />

      {tabs.length > 1 && (
        <div role="tablist" className="flex flex-wrap gap-1 border-b border-border/60">
          {tabs.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              onClick={() => setTab(t.key)}
              className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
                tab === t.key
                  ? 'border-primary font-semibold text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t.label}
              {t.count !== undefined ? <span className="ml-1.5 text-xs text-muted-foreground">({t.count})</span> : null}
            </button>
          ))}
        </div>
      )}

      {body}
    </ScrollPage>
  );
}
